// The randomized tests of a learner's first experience (owner's decisions, 2026-10-07), and the one place
// that decides who gets which side. The tests, their ids and how they fit together are described in
// AGENTS.md ("First-experience tests"); the rules that make them gel are in firstExperienceGelRules.ts.
//
// This file is imported by code that runs at app startup (the review's navigation, the analytics
// identify call, AuthContext). Keep it small: its only import is the analytics device id, and it reads
// localStorage, sessionStorage and the URL, nothing else.
//
// Assignment
//   bucket = the high bits of a 32-bit hash of "<experiment id>:<assignment unit id>", scaled to 0–99. The
//   hash is FNV-1a (the paywall tests' hash, upsellFlowConfig.ts) followed by MurmurHash3's final mix: FNV-1a
//   alone passes its lowest bits through almost unmixed, so two tests' buckets taken modulo 100 always agreed
//   on being even or odd. With the mix, each test's draw is independent of every other's.
//
//   The assignment unit is the person, as far as the app can tell:
//     signed out  the analytics device id (localStorage "deviceUID")
//     signed in   the account's `experiment_assignment_unit_id`. A new account copies the device id of the
//                 browser it signs up in (claimThisDeviceAsAssignmentUnitForNewAccount), so a visitor keeps
//                 their draws through sign up; a second account made in the same browser gets its own
//                 (its uid), because the browser's id already belongs to the first. An account without the
//                 field (made before these tests, or by the Worker's partner sign up) uses its uid.
//
// Where a side comes from, first match wins
//   1. a side forced for QA (below);
//   2. signed in: the side the account holds (`users.experiment_variants`, by experiment id), the same on
//      every device. A side is written there the first time the account is exposed to it;
//   3. signed in: this account's own record on this device (`first_experience_test:<id>:account:<uid>`),
//      for the moment between an exposure and its account write arriving;
//   4. signed in, a test that signed-out visitors take part in: the side this device showed while signed out
//      (`first_experience_test:<id>:<device id>`), unless another account already took that one over;
//   5. a draw from the assignment unit.
//   Signed out: this device's record, else a draw from the device id.
//
// Exposure
//   `First Experience Test Assigned` {experiment_id, first_experience_variant, first_experience_bucket}, the
//   first time the test changes (or could change) what the person sees (trackFirstExperienceTestExposure.ts):
//   once per signed-out device, and once per account (whatever device; on its first exposure the side is
//   written to the account). A visitor exposed signed out who then signs in is reported twice: on the
//   device's profile, and on the account's when the account takes the side over. The side also goes on the
//   OpenPanel person record (`first_experience_test_<experiment id>`), read from the account first, so every
//   device sends the same value: OpenPanel does not link a signed-out device's events to the account made
//   afterwards (the SDK's alias() is a no-op, and the Jungle device id is not sent to OpenPanel).
//
// QA and the demo app
//   `?first_experience_preview=<variant>[,<variant>...]` forces those sides for the rest of the tab
//   (`?first_experience_preview=off` forgets them). Development builds only. A forced side is never
//   recorded, reported or written to an account, so it stays out of the experiment.

import { getOrCreateDeviceUID } from "../backend/utils/device-identity";

export type FirstExperienceTestScope =
  // Anyone, signed in or not: the draw follows the visitor into the account.
  | "visitor"
  // Signed-in accounts only.
  | "account";

export type FirstExperienceTestDefinition<Variant extends string> = {
  experimentId: string;
  // The first variant is today's experience (the control), the side shown outside the test. T1 (ended) was the one
  // test without one: both its sides were new.
  variants: readonly Variant[];
  // Exclusive upper bucket of each variant, in the same order; the last is 100.
  variantUpperBuckets: readonly number[];
  scope: FirstExperienceTestScope;
};

function defineFirstExperienceTest<Variant extends string>(
  definition: FirstExperienceTestDefinition<Variant>
): FirstExperienceTestDefinition<Variant> {
  return definition;
}

/**
 * T1. A signed-out visitor's first set: sign up on the questions screen before answering ("save first") or after
 * answering the first three questions ("answer first"); owner, 2026-10-09: "sign up after the preview screen vs after
 * 3 questions".
 * ENDED 2026-10-19: save_first won (new accounts 53.6% vs 43.6% of exposed visitors, −19% on answer_first, p≈1e-10;
 * revenue not measurable at this traffic); the owner cemented sign-up after the preview for everyone, including
 * signed-in learners' first sets. Nothing reads this test's side or reports its exposure. The definition stays so its
 * id is never reused.
 */
export const ANSWER_FIRST_TEST = defineFirstExperienceTest({
  experimentId: "answer_first_2026_10",
  variants: ["save_first", "answer_first"] as const,
  variantUpperBuckets: [50, 100],
  scope: "visitor",
});

/**
 * T2. How many questions the first review (the first set's mini review) has: 7 (control), 5 or 10.
 * ENDED 2026-10-27: 5 won (78.6% of those who started it finished it, vs 75.9% on 7 and 71.6% on 10; first-day
 * answers, returns and purchases the same or too small to tell apart, and 10 the worst on every count) and the owner
 * cemented it: every first review asks FIRST_REVIEW_QUESTION_COUNT (5) questions, and nothing reads this test's side
 * or reports its exposure. The definition stays so its id is never reused.
 */
export const FIRST_REVIEW_LENGTH_TEST = defineFirstExperienceTest({
  experimentId: "first_review_length_2026_10",
  // A third each: 34, 33 and 33 buckets. The control (today's 7) comes first, as in every test, because it is
  // the side shown outside the test (getFirstExperienceTestVariant), and it holds the one bucket left over.
  variants: ["7_questions", "5_questions", "10_questions"] as const,
  variantUpperBuckets: [34, 67, 100],
  scope: "visitor",
});

/**
 * T3. The first review starts with an easy win: a true/false question, then the easiest multiple choice.
 * ENDED 2026-10-15: the easy start won (answered again within 7 days 12.3% vs 9.3%, p=0.01) and the owner cemented
 * it: every first review now opens easy and nothing reads this test's side or reports its exposure. The definition
 * stays so its id is never reused and the sides already on accounts stay recognizable.
 */
export const EASY_START_TEST = defineFirstExperienceTest({
  experimentId: "easy_start_2026_10",
  variants: ["current_order", "easy_start"] as const,
  variantUpperBuckets: [50, 100],
  scope: "visitor",
});

/** T2b. The one-minute voice check during the first review, before its "great work" checkpoint. */
export const FIRST_REVIEW_VOICE_CHECK_TEST = defineFirstExperienceTest({
  experimentId: "first_review_voice_check_2026_10",
  variants: ["no_voice_check", "voice_check"] as const,
  variantUpperBuckets: [50, 100],
  scope: "visitor",
});

/**
 * T2c. For a health student on T2b's "voice_check" side, the OSCE station of the first set instead of the voice
 * check at the end of the first review (owner, 2026-10-15: "for health students, in their first user experience
 * ... I want to try ... showing the OSCE station instead"). A first set with no station due gets the voice check on
 * both sides; the report counts the learner on their drawn side anyway and shows how many saw a station.
 */
export const FIRST_REVIEW_OSCE_STATION_TEST = defineFirstExperienceTest({
  experimentId: "first_review_osce_station_2026_10",
  variants: ["keep_voice_check", "osce_station"] as const,
  variantUpperBuckets: [50, 100],
  scope: "account",
});

/** T5. The free-generation page, written for an account's first set. */
export const FIRST_SET_LIMIT_PAGE_TEST = defineFirstExperienceTest({
  experimentId: "first_set_limit_page_2026_10",
  variants: ["current_page", "first_set_page"] as const,
  variantUpperBuckets: [50, 100],
  scope: "account",
});

/** T5b. New accounts in the one-free-generation countries get everyone's two. */
export const FREE_ALLOWANCE_TEST = defineFirstExperienceTest({
  experimentId: "free_allowance_2026_10",
  variants: ["one_generation", "two_generations"] as const,
  variantUpperBuckets: [50, 100],
  scope: "account",
});

/**
 * T6. The upsell after the first review for a learner whose exam is close: production's Elite first pop-up, or the
 * exam pass (owner, 2026-10-18: one payment for Elite until the exam, beside Elite yearly, drawn as two tickets).
 * Only signed-in Free learners on the web whose exam date, saved on the exam question before the checkpoint, is 0–30
 * days away (`getExamPassTierForExam`, firstExperienceGelRules.ts). The control side's name `a2_upsell` is from
 * before 2026-10-21, when the control was A2 ("Simplified"); it stays so the sides already held keep their meaning.
 */
export const EXAM_PASS_UPSELL_TEST = defineFirstExperienceTest({
  experimentId: "first_upsell_exam_pass_2026_10",
  variants: ["a2_upsell", "exam_pass"] as const,
  variantUpperBuckets: [50, 100],
  scope: "account",
});

export const FIRST_EXPERIENCE_TESTS = [
  ANSWER_FIRST_TEST,
  FIRST_REVIEW_LENGTH_TEST,
  EASY_START_TEST,
  FIRST_REVIEW_VOICE_CHECK_TEST,
  FIRST_REVIEW_OSCE_STATION_TEST,
  FIRST_SET_LIMIT_PAGE_TEST,
  FREE_ALLOWANCE_TEST,
  EXAM_PASS_UPSELL_TEST,
] as const;

export type AnyFirstExperienceTest = (typeof FIRST_EXPERIENCE_TESTS)[number];
export type VariantOf<Test> = Test extends FirstExperienceTestDefinition<infer Variant> ? Variant : never;

export const FIRST_EXPERIENCE_TEST_ASSIGNED_EVENT = "First Experience Test Assigned";
export const FIRST_EXPERIENCE_TEST_PERSON_PROPERTY_PREFIX = "first_experience_test_";
export const FIRST_EXPERIENCE_PREVIEW_URL_PARAMETER = "first_experience_preview";
const FIRST_EXPERIENCE_PREVIEW_SESSION_STORAGE_KEY = "first_experience_preview";

/** The parts of the signed-in account the assignment reads. Null or undefined when signed out. */
export type FirstExperienceTestViewer =
  | {
      id?: string | null;
      experiment_assignment_unit_id?: string | null;
      // The sides the account holds, by experiment id (written on its first exposure to each test)
      experiment_variants?: Record<string, string> | null;
    }
  | null
  | undefined;

export type FirstExperienceTestSideSource =
  "forced_for_qa" | "held_by_account" | "account_record_on_this_device" | "signed_out_record_on_this_device" | "draw";

export type FirstExperienceTestAssignment<Variant extends string = string> = {
  experimentId: string;
  variant: Variant;
  // 0–99; -1 when forced for QA.
  bucket: number;
  // The id the bucket was drawn from.
  assignmentUnitId: string;
  // The signed-in account the side was resolved for, null signed out.
  accountId: string | null;
  sideSource: FirstExperienceTestSideSource;
  // Forced by `?first_experience_preview`: never recorded, never reported.
  isForcedForQa: boolean;
  // A side recorded or held earlier (as opposed to a draw made now).
  isPersistedFirstExposure: boolean;
};

/** 0–99 from the high bits of FNV-1a (32-bit) of the salted id, after MurmurHash3's final mix. */
export function getFirstExperienceTestBucket(experimentId: string, assignmentUnitId: string): number {
  const saltedId = `${experimentId}:${assignmentUnitId}`;
  let hash = 0x811c9dc5;
  for (let index = 0; index < saltedId.length; index += 1) {
    hash ^= saltedId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  // MurmurHash3's fmix32: spreads every input bit over every output bit
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return Math.floor(((hash >>> 0) * 100) / 2 ** 32);
}

export function getFirstExperienceTestVariantForBucket<Variant extends string>(
  test: FirstExperienceTestDefinition<Variant>,
  bucket: number
): Variant {
  const variantIndex = test.variantUpperBuckets.findIndex((upperBucket) => bucket < upperBucket);
  return test.variants[variantIndex === -1 ? test.variants.length - 1 : variantIndex];
}

function readThisDeviceId(): string | null {
  // The analytics device id (created when missing), so a visitor's draw does not depend on whether an event
  // was tracked first.
  try {
    return getOrCreateDeviceUID();
  } catch {
    return null;
  }
}

/** The account's assignment unit: its stored unit id, else its uid. Null when signed out. */
export function getAccountAssignmentUnitId(viewer: FirstExperienceTestViewer): string | null {
  if (!viewer?.id) return null;
  return viewer.experiment_assignment_unit_id || viewer.id;
}

function getAssignmentUnitId(test: FirstExperienceTestDefinition<string>, viewer: FirstExperienceTestViewer) {
  const accountAssignmentUnitId = getAccountAssignmentUnitId(viewer);
  if (accountAssignmentUnitId) return accountAssignmentUnitId;
  return test.scope === "visitor" ? readThisDeviceId() : null;
}

// ───────────────────────── a new account's assignment unit ─────────────────────────

const DEVICE_ASSIGNMENT_UNIT_CLAIMED_BY_STORAGE_KEY = "first_experience_device_unit_claimed_by";

/**
 * The assignment unit a new account should keep: this browser's device id, so a visitor keeps their draws
 * through sign up, unless another account made in this browser already holds it. Then null, and the
 * account uses its own uid: each account gets its own draws. Called by createUserOnFirebase.
 */
export function claimThisDeviceAsAssignmentUnitForNewAccount(accountId: string): string | null {
  const thisDeviceId = readThisDeviceId();
  if (!thisDeviceId) return null;
  try {
    const claimedByAccountId = localStorage.getItem(DEVICE_ASSIGNMENT_UNIT_CLAIMED_BY_STORAGE_KEY);
    if (claimedByAccountId && claimedByAccountId !== accountId) return null;
    localStorage.setItem(DEVICE_ASSIGNMENT_UNIT_CLAIMED_BY_STORAGE_KEY, accountId);
    return thisDeviceId;
  } catch {
    // Without storage this browser cannot tell a second account from the first: each uses its uid
    return null;
  }
}

// ───────────────────────── exposures recorded on this device ─────────────────────────

type RecordedFirstExposure = { variant: string; bucket: number; claimedByAccountId?: string };

/** A signed-out visitor's record on this device. */
export function getFirstExperienceTestExposureStorageKey(experimentId: string, deviceId: string): string {
  return `first_experience_test:${experimentId}:${encodeURIComponent(deviceId)}`;
}

/** A signed-in account's record on this device. */
export function getFirstExperienceTestAccountExposureStorageKey(experimentId: string, accountId: string): string {
  return `first_experience_test:${experimentId}:account:${encodeURIComponent(accountId)}`;
}

// When localStorage cannot be written (some WebViews, a full disk), the record is kept for the rest of the
// page load instead, so the side cannot change and the exposure is not reported twice.
const recordsKeptInMemory = new Map<string, RecordedFirstExposure>();

function readRecord(test: FirstExperienceTestDefinition<string>, storageKey: string): RecordedFirstExposure | null {
  let storedValue: string | null = null;
  try {
    storedValue = localStorage.getItem(storageKey);
  } catch {
    // Storage unavailable: this page load's memory below.
  }
  if (storedValue) {
    try {
      const { variant, bucket, claimedByAccountId } = JSON.parse(storedValue) ?? {};
      const isValidBucket = Number.isInteger(bucket) && bucket >= 0 && bucket < 100;
      if ((test.variants as readonly string[]).includes(variant) && isValidBucket) {
        return typeof claimedByAccountId === "string" ? { variant, bucket, claimedByAccountId } : { variant, bucket };
      }
    } catch {
      // A damaged record counts as none.
    }
  }
  return recordsKeptInMemory.get(storageKey) ?? null;
}

function writeRecord(storageKey: string, record: RecordedFirstExposure): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(record));
  } catch {
    // Storage unavailable: keep it for this page load.
    recordsKeptInMemory.set(storageKey, record);
  }
}

/** This device's signed-out record that the account may take over: none taken over by another account. */
function readSignedOutRecordForAccount(
  test: FirstExperienceTestDefinition<string>,
  accountId: string
): { record: RecordedFirstExposure; deviceId: string } | null {
  if (test.scope !== "visitor") return null;
  const thisDeviceId = readThisDeviceId();
  if (!thisDeviceId) return null;
  const record = readRecord(test, getFirstExperienceTestExposureStorageKey(test.experimentId, thisDeviceId));
  if (!record || (record.claimedByAccountId && record.claimedByAccountId !== accountId)) return null;
  return { record, deviceId: thisDeviceId };
}

function readSideHeldByAccount(test: FirstExperienceTestDefinition<string>, viewer: FirstExperienceTestViewer) {
  const sideHeldByAccount = viewer?.id ? viewer.experiment_variants?.[test.experimentId] : undefined;
  return sideHeldByAccount && (test.variants as readonly string[]).includes(sideHeldByAccount)
    ? sideHeldByAccount
    : null;
}

// ───────────────────────── QA switches ─────────────────────────

// T6's two sides may also be forced in a production build (owner, 2026-10-18: "give me a link to try it"), so the
// exam pass can be tried end to end on a fresh account at app.jungleai.com/?first_experience_preview=exam_pass. A
// forced side is never recorded, reported or written to an account, so the test's counts stay clean.
const VARIANTS_FORCEABLE_IN_PRODUCTION = new Set(["exam_pass", "a2_upsell"]);

function readVariantsForcedForQa(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const valueFromUrl = new URLSearchParams(window.location.search).get(FIRST_EXPERIENCE_PREVIEW_URL_PARAMETER);
    if (valueFromUrl === "off") {
      sessionStorage.removeItem(FIRST_EXPERIENCE_PREVIEW_SESSION_STORAGE_KEY);
      return [];
    }
    if (valueFromUrl) sessionStorage.setItem(FIRST_EXPERIENCE_PREVIEW_SESSION_STORAGE_KEY, valueFromUrl);
    const forcedValue = valueFromUrl ?? sessionStorage.getItem(FIRST_EXPERIENCE_PREVIEW_SESSION_STORAGE_KEY) ?? "";
    const forcedVariants = forcedValue
      .split(",")
      .map((variant) => variant.trim())
      .filter(Boolean);
    return import.meta.env.DEV
      ? forcedVariants
      : forcedVariants.filter((variant) => VARIANTS_FORCEABLE_IN_PRODUCTION.has(variant));
  } catch {
    return [];
  }
}

/** The side forced by `?first_experience_preview` for this test (development builds; T6's sides in production too), else null. */
export function getFirstExperienceTestVariantForcedForQa<Variant extends string>(
  test: FirstExperienceTestDefinition<Variant>
): Variant | null {
  const forcedVariants = readVariantsForcedForQa();
  return test.variants.find((variant) => forcedVariants.includes(variant)) ?? null;
}

// ───────────────────────── resolving a side ─────────────────────────

/**
 * The person's side of a test, from the first of: a side forced for QA, the account's held side, this
 * account's record on this device, this device's signed-out record (visitor tests), a draw. Null outside the
 * test (an account test while signed out, or no id to draw from); callers then show today's experience.
 */
export function resolveFirstExperienceTestAssignment<Variant extends string>(
  test: FirstExperienceTestDefinition<Variant>,
  viewer: FirstExperienceTestViewer
): FirstExperienceTestAssignment<Variant> | null {
  const accountId = viewer?.id || null;
  const variantForcedForQa = getFirstExperienceTestVariantForcedForQa(test);
  if (variantForcedForQa) {
    return {
      experimentId: test.experimentId,
      variant: variantForcedForQa,
      bucket: -1,
      assignmentUnitId: "qa-preview",
      accountId,
      sideSource: "forced_for_qa",
      isForcedForQa: true,
      isPersistedFirstExposure: false,
    };
  }

  const assignmentUnitId = getAssignmentUnitId(test, viewer);
  if (!assignmentUnitId) return null;
  const fromRecord = (
    record: RecordedFirstExposure,
    unitIdOfRecord: string,
    sideSource: FirstExperienceTestSideSource
  ): FirstExperienceTestAssignment<Variant> => ({
    experimentId: test.experimentId,
    variant: record.variant as Variant,
    bucket: record.bucket,
    assignmentUnitId: unitIdOfRecord,
    accountId,
    sideSource,
    isForcedForQa: false,
    isPersistedFirstExposure: true,
  });

  if (accountId) {
    const sideHeldByAccount = readSideHeldByAccount(test, viewer);
    if (sideHeldByAccount) {
      return {
        experimentId: test.experimentId,
        variant: sideHeldByAccount as Variant,
        bucket: getFirstExperienceTestBucket(test.experimentId, assignmentUnitId),
        assignmentUnitId,
        accountId,
        sideSource: "held_by_account",
        isForcedForQa: false,
        isPersistedFirstExposure: true,
      };
    }
    const accountRecord = readRecord(
      test,
      getFirstExperienceTestAccountExposureStorageKey(test.experimentId, accountId)
    );
    if (accountRecord) return fromRecord(accountRecord, assignmentUnitId, "account_record_on_this_device");
    const signedOutRecord = readSignedOutRecordForAccount(test, accountId);
    if (signedOutRecord) {
      return fromRecord(signedOutRecord.record, signedOutRecord.deviceId, "signed_out_record_on_this_device");
    }
  } else {
    const signedOutRecord = readRecord(
      test,
      getFirstExperienceTestExposureStorageKey(test.experimentId, assignmentUnitId)
    );
    if (signedOutRecord) return fromRecord(signedOutRecord, assignmentUnitId, "signed_out_record_on_this_device");
  }

  const bucket = getFirstExperienceTestBucket(test.experimentId, assignmentUnitId);
  return {
    experimentId: test.experimentId,
    variant: getFirstExperienceTestVariantForBucket(test, bucket),
    bucket,
    assignmentUnitId,
    accountId,
    sideSource: "draw",
    isForcedForQa: false,
    isPersistedFirstExposure: false,
  };
}

/** The person's side, or today's experience (the first variant) outside the test. */
export function getFirstExperienceTestVariant<Variant extends string>(
  test: FirstExperienceTestDefinition<Variant>,
  viewer: FirstExperienceTestViewer
): Variant {
  return resolveFirstExperienceTestAssignment(test, viewer)?.variant ?? test.variants[0];
}

/**
 * Records the first exposure on this device. True only when the exposure event should be sent: the first
 * exposure of a signed-out device, or of an account that does not hold the side yet. False for a side
 * forced for QA, a side the account already holds (reported on another device), or one recorded before.
 * A signed-out record the account takes over is marked as taken, so another account in this browser does
 * not take it too.
 */
export function recordFirstExperienceTestFirstExposure(assignment: FirstExperienceTestAssignment): boolean {
  if (assignment.isForcedForQa || assignment.sideSource === "held_by_account") return false;
  const test = FIRST_EXPERIENCE_TESTS.find((candidate) => candidate.experimentId === assignment.experimentId);
  if (!test) return false;
  const exposure: RecordedFirstExposure = { variant: assignment.variant, bucket: assignment.bucket };

  if (!assignment.accountId) {
    const deviceStorageKey = getFirstExperienceTestExposureStorageKey(
      assignment.experimentId,
      assignment.assignmentUnitId
    );
    if (readRecord(test, deviceStorageKey)) return false;
    writeRecord(deviceStorageKey, exposure);
    return true;
  }

  const accountStorageKey = getFirstExperienceTestAccountExposureStorageKey(
    assignment.experimentId,
    assignment.accountId
  );
  if (readRecord(test, accountStorageKey)) return false;
  writeRecord(accountStorageKey, exposure);
  if (assignment.sideSource === "signed_out_record_on_this_device") {
    const deviceStorageKey = getFirstExperienceTestExposureStorageKey(
      assignment.experimentId,
      assignment.assignmentUnitId
    );
    writeRecord(deviceStorageKey, { ...exposure, claimedByAccountId: assignment.accountId });
  }
  return true;
}

/** The exposure event's properties: exactly these three, as in `Paywall Copy Experiment Assigned`. */
export function getFirstExperienceTestExposureEventProperties(assignment: FirstExperienceTestAssignment) {
  return {
    experiment_id: assignment.experimentId,
    first_experience_variant: assignment.variant,
    first_experience_bucket: assignment.bucket,
  };
}

/**
 * For "Mini Lesson Completed Page Visited": how many questions the first review actually asked (a set short of
 * questions, or still generating, can end it before its 5), so a readout can see how often that happens.
 */
export function getFirstReviewCompletionEventProperties(numberOfQuestionsAsked: number) {
  return { first_review_question_count: numberOfQuestionsAsked };
}

/**
 * The person's exposed sides, for the OpenPanel person record: one `first_experience_test_<experiment id>`
 * property per test, from the side the account holds, else this device's record for the person. This is
 * what lets the reports join an exposure to the account's later events, the same on every device.
 */
export function getFirstExperienceTestPersonProperties(viewer: FirstExperienceTestViewer): Record<string, string> {
  const personProperties: Record<string, string> = {};
  for (const test of FIRST_EXPERIENCE_TESTS) {
    const propertyName = `${FIRST_EXPERIENCE_TEST_PERSON_PROPERTY_PREFIX}${test.experimentId}`;
    const sideHeldByAccount = readSideHeldByAccount(test, viewer);
    if (sideHeldByAccount) {
      personProperties[propertyName] = sideHeldByAccount;
      continue;
    }
    let recordForPerson: RecordedFirstExposure | null = null;
    if (viewer?.id) {
      recordForPerson =
        readRecord(test, getFirstExperienceTestAccountExposureStorageKey(test.experimentId, viewer.id)) ??
        readSignedOutRecordForAccount(test, viewer.id)?.record ??
        null;
    } else {
      const thisDeviceId = test.scope === "visitor" ? readThisDeviceId() : null;
      recordForPerson = thisDeviceId
        ? readRecord(test, getFirstExperienceTestExposureStorageKey(test.experimentId, thisDeviceId))
        : null;
    }
    if (recordForPerson) personProperties[propertyName] = recordForPerson.variant;
  }
  return personProperties;
}

// The app rewrites its address soon after it loads (AppRouter re-appends `r`, a generation drops
// `autoGenerate`), so the QA switch is read once as this module loads, while it is still in the address.
readVariantsForcedForQa();
